import { initializeApp } from "firebase/app"
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check"
import {
    Auth,
    getAuth,
    createUserWithEmailAndPassword,
    sendEmailVerification,
    signInWithEmailAndPassword,
    applyActionCode,
    verifyPasswordResetCode,
    confirmPasswordReset,
    NextOrObserver,
    User as FIRUser,
    onAuthStateChanged,
    OAuthProvider,
    getRedirectResult,
    signInWithRedirect
} from "firebase/auth"
import {
    initializeFirestore,
    Firestore,
    Timestamp,
    setDoc,
    doc,
    getDoc,
    getDocs,
    query,
    collection,
    deleteDoc,
    addDoc,
    where,
    updateDoc,
    collectionGroup,
    orderBy,
    startAt,
    endAt,
    serverTimestamp
} from "firebase/firestore"
import { Functions, getFunctions, httpsCallable } from "firebase/functions"
import { FirebaseStorage, getDownloadURL, getStorage, ref, uploadString } from "firebase/storage"
import moment from "moment-timezone"
import "../Firebase/Models-dist/extensions"
import {
    Reservation,
    VisitorReservation,
    User,
    Car,
    ParkingSpot,
    ParkingSize,
    AbuseInfo,
    ApiError,
    Location,
    Collections,
    Company,
    PermanentReservation,
    EngineType,
    UserPriorityLevel,
    ReservationResult,
    ReservationResultState,
    ApiErrorCode,
    FleetReservationResult,
    ReservationStatus
} from "."

import { firebaseConfig } from "../../constants/config.js"
import { FleetCar } from "./Models-dist/fleetCar"
import { FleetReservation } from "./Models-dist/fleetReservation"
import { Fleet } from "./Models-dist/fleet"
import packageJson from "../../../package.json"

class Firebase {
    private readonly firestore: Firestore
    private readonly auth: Auth
    private readonly storage: FirebaseStorage
    private readonly functions: Functions

    constructor() {
        const app = initializeApp(firebaseConfig)

        // Use this to log appcheck debug token to console - than copy paste this debug token to Firebase -> staging -> AppCheck -> Garage web staging legacy
        // ;(window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true

        initializeAppCheck(app, {
            provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY!),
            isTokenAutoRefreshEnabled: true
        })

        this.firestore = initializeFirestore(app, { experimentalAutoDetectLongPolling: true, ignoreUndefinedProperties: true })
        this.auth = getAuth(app)
        this.storage = getStorage(app)
        this.functions = getFunctions(app, "europe-west1")
    }

    generateReservationId = (locationId: string): string => {
        const col = collection(this.firestore, Collections.Parking, locationId, Collections.Reservations)
        return doc(col).id
    }

    // *** Auth API ***

    createUserWithEmailAndPassword = (email: string, password: string) => {
        return createUserWithEmailAndPassword(this.auth, email, password).then(authUser => {
            return Promise.resolve(authUser)
        })
    }

    deleteAuthUser = (authUser: FIRUser) => {
        return authUser.delete()
    }

    sendVerificationEmail = () => {
        if (this.auth.currentUser) {
            sendEmailVerification(this.auth.currentUser)
        }
    }

    signInWithEmailAndPassword = (email: string, password: string) => signInWithEmailAndPassword(this.auth, email, password)

    singInWithSSO = (providerId: string, email: string) => {
        const provider = new OAuthProvider(providerId)
        provider.setCustomParameters({
            login_hint: email
        })
        //TODO probably remove this (okta specifics)
        //provider.addScope('okta.users.read.self')
        provider.addScope("email")
        provider.addScope("profile")
        provider.addScope("address")
        return signInWithRedirect(this.auth, provider)
    }

    getRedirectResult = () => {
        return getRedirectResult(this.auth)
    }

    signOut = () => this.auth.signOut()

    verifyEmail = (actionCode: string) => applyActionCode(this.auth, actionCode)

    verifyPasswordResetCode = (actionCode: string) => verifyPasswordResetCode(this.auth, actionCode)

    confirmPasswordReset = (actionCode: string, newPassword: string) => confirmPasswordReset(this.auth, actionCode, newPassword)

    onAuthStateChanged = (nextOrObserver: NextOrObserver<FIRUser>) => onAuthStateChanged(this.auth, nextOrObserver)

    // *** User data API ***
    fetchUserData = (userId: string) => {
        const ref = doc(this.firestore, Collections.Users, userId)
        return getDoc(ref).then(doc => {
            if (doc.exists()) {
                const user = User.fromData(doc)
                return Promise.resolve(user)
            } else {
                return Promise.reject(new ApiError(`Getting user ${userId} failed, empty data`))
            }
        })
    }

    fetchUserPrivateData = async (user: User) => {
        const ref = doc(this.firestore, Collections.Users, user.uid, Collections.UserPrivate, Collections.UserContactData)
        const snap = await getDoc(ref)
        if (snap.exists()) {
            const data = snap.data()!
            user.email = data.email
            user.phone = data.phone
            user.surname = data.surname
        }
        return user
    }

    fetchUserDataWithCars = async (userId: string, includePrivate: boolean = false, includeLocationsAndFleets: boolean = true) => {
        const [userResult, carResult] = await Promise.allSettled([this.fetchUserData(userId), this.fetchCarsForUserId(userId)])
        if (userResult.status === "rejected") {
            console.log(`Fetching user ${userId} with cars failed (because of user): `, userResult.reason)
            return Promise.resolve(null)
        } else if (carResult.status === "rejected") {
            console.log(`Fetching user ${userId} with cars failed (because of car): `, carResult.reason)
            return Promise.resolve(null)
        }
        const user = userResult.value
        if (user && user.locationIds) {
            if (includePrivate) {
                try {
                    await this.fetchUserPrivateData(user)
                } catch (error) {
                    console.log(`Error fetching private data of user ${user.uid}: `, error)
                }
            }
            user.cars = carResult.value

            if (includeLocationsAndFleets) {
                const locationResults = await Promise.allSettled(user.locationIds.map(this.fetchLocation))
                user.locations = locationResults
                    .map(result => {
                        if (result.status === "rejected") {
                            console.log(`Skipping inaccessible location of user ${user.uid}: `, result.reason)
                            return undefined
                        }
                        return result.value
                    })
                    .filterUndef()

                const fleetResults = await Promise.allSettled(user.fleetIds.map(this.fetchFleet))
                user.fleets = fleetResults
                    .map(result => {
                        if (result.status === "rejected") {
                            console.log(`Skipping inaccessible fleet of user ${user.uid}: `, result.reason)
                            return undefined
                        }
                        return result.value
                    })
                    .filterUndef()
            }

            return Promise.resolve(user)
        }
        return Promise.resolve(null)
    }

    setAuthLocale = (locale: string) => {
        this.auth.languageCode = locale
    }

    setUser = async (user: User) => {
        const ref = doc(this.firestore, Collections.Users, user.uid)
        await setDoc(
            ref,
            {
                timestampUpdated: serverTimestamp(),
                name: user.name,
                surname: user.surname.substring(0, 1),
                email: "",
                company: user.company ?? "",
                priority: user.priority ?? 0,
                locations: user.locationIds ?? null,
                fleets: user.fleetIds ?? null,
                visitorCompany: user.visitorCompany ?? null,
                host: user.host ?? null,
                locale: user.locale ?? "en"
            },
            { merge: true }
        ).catch(error => {
            console.log("Error setting user: ", error)
        })
        const privateDataRef = doc(this.firestore, Collections.Users, user.uid, Collections.UserPrivate, Collections.UserContactData)
        await setDoc(
            privateDataRef,
            {
                timestampUpdated: serverTimestamp(),
                surname: user.surname,
                phone: user.phone ?? "",
                email: user.email
            },
            { merge: true }
        ).catch(error => {
            console.log("Error setting user private data: ", error)
        })
    }

    setUserLocale = (userId: string, locale: string) => {
        const ref = doc(this.firestore, Collections.Users, userId)
        return setDoc(
            ref,
            {
                locale: locale
            },
            { merge: true }
        )
    }

    setUserActiveLocation = (user: User, locationId: string, providedLocations: Location[] | undefined = undefined) => {
        const oldLocations = user.locationIds ?? []
        let locations = [locationId]
        locations = locations.concat(oldLocations.filter(l => l !== locationId))
        const locationsData = [...(user.locations ?? []), ...(providedLocations ?? [])]
        user.locations = locations
            .map(locationId => locationsData.find(l => l.id === locationId))
            .filter(l => l)
            .map(l => l!)
        const ref = doc(this.firestore, Collections.Users, user.uid)
        return setDoc(
            ref,
            {
                locations: locations
            },
            { merge: true }
        )
    }

    deleteUser = (userId: string) => {
        const ref = doc(this.firestore, Collections.Users, userId)
        return setDoc(
            ref,
            {
                deleted: true
            },
            { merge: true }
        )
    }

    enableDisableUser = (userId: string, mode: "enable" | "disable") => {
        return this.callAdminApi("enableDisableUser", { targetUserId: userId, mode })
    }

    getUsersLastActiveTime = (user: User) => {
        return user.lastActiveGarageWeb
    }

    setUsersLastActiveTime = (uid: string) => {
        const ref = doc(this.firestore, Collections.Users, uid)
        return setDoc(
            ref,
            {
                lastActiveGarageWeb: serverTimestamp()
            },
            { merge: true }
        ).catch(error => {
            console.log("Error setting user: ", error)
        })
    }

    // In out optional user gets filled with cars if provided
    fetchCarsForUserId = (userId: string, user: User | undefined = undefined) => {
        const q = query(collection(this.firestore, Collections.Users, userId, Collections.Cars))
        return getDocs(q).then((querySnapshot: any) => {
            let cars: Car[] = []
            querySnapshot.forEach((doc: any) => {
                const car = Car.fromData(doc)
                if (car) {
                    cars.push(car)
                }
            })
            if (user) user.cars = cars
            return Promise.resolve(cars)
        })
    }

    deleteCarForUserId = (carId: string, userId: string) => {
        const ref = doc(this.firestore, Collections.Users, userId, Collections.Cars, carId)
        return deleteDoc(ref)
    }

    fetchCarForUserId = (carId: string, userId: string) => {
        const ref = doc(this.firestore, Collections.Users, userId, Collections.Cars, carId)
        return getDoc(ref).then(Car.fromData)
    }

    generateCarId = (userId: string) => {
        return doc(collection(this.firestore, Collections.Users, userId, Collections.Cars)).id
    }

    /**
     * Direct Firestore write, kept ONLY for the visitor-booking flow: it creates a car for a *different*
     * user under a client-generated id that the visitor reservation references. The `createCar` callable
     * cannot serve that case - it rejects a userId other than the caller's own uid and mints its own id.
     *
     * Everything a signed-in user does for themselves must go through `createCarAPI` instead.
     *
     * Longer term this whole path is superseded by the `adminApi` `createVisitorBooking` callable, which
     * already creates the visitor user, car, and reservation in one transaction.
     */
    createVisitorCar = (car: Car, userId: string) => {
        const carId = car.id ? car.id : this.generateCarId(userId)
        const ref = doc(this.firestore, Collections.Users, userId, Collections.Cars, carId)
        return setDoc(
            ref,
            {
                timestampCreated: serverTimestamp(),
                plateNum: Car.normalizePlate(car.plateNum),
                brand: car.brand ?? null,
                model: car.model ?? null,
                color: car.color ?? null,
                engineType: car.engineType
            },
            { merge: true }
        )
    }

    /**
     * Creates a car through the backend `createCar` callable, which owns the write and validates the
     * payload (the size against the ParkingSize enum, and the plate, which it normalises server-side).
     * This is the path for anything a signed-in user does for themselves; see `createVisitorCar` above
     * for the one remaining case that cannot use it.
     *
     * Blank optional fields are sent as undefined rather than "" so we do not store empty strings.
     */
    createCarAPI = async (car: Car, userId: string): Promise<string> => {
        const requestBody = {
            userId: userId,
            plateNum: car.plateNum,
            brand: car.brand || undefined,
            model: car.model || undefined,
            color: car.color || undefined,
            engineType: car.engineType,
            size: car.size
        }

        const { data } = await this.callClientApi("createCar", requestBody)

        if (data.status !== "OK") {
            if (data.errorCode !== undefined) {
                throw new ApiError("Create car error " + ApiErrorCode[data.errorCode], data.errorCode)
            }
            throw new Error(`Unknown create car error: ${data}`)
        }
        if (typeof data.carId !== "string") {
            throw new Error(`Unknown create car error: ${JSON.stringify(data)}`)
        }
        return data.carId
    }

    fetchAllUsers = async (companyCode: string) => {
        const q = query(collection(this.firestore, Collections.Users), where("company", "==", companyCode))
        const querySnapshot = await getDocs(q)

        const users = querySnapshot.docs
            .map(User.fromData)
            .filter(u => u)
            .map(u => u!)

        const privateResults = await Promise.allSettled(users.map(this.fetchUserPrivateData))
        privateResults.forEach((result, index) => {
            if (result.status === "rejected") {
                console.log(`Skipping inaccessible private data of user ${users[index].uid}: `, result.reason)
            }
        })

        return users.filter(u => !u.deleted)
    }

    fetchUsersWithPrivateData = async (userIds: string[]) => {
        const users: User[] = (await Promise.all(userIds.map(this.fetchUserData))).filter((it): it is User => it !== null)
        await Promise.all(users.map(this.fetchUserPrivateData))
        return users
    }

    lookupLicense = async (licensePlate: string): Promise<[User, Car] | null> => {
        const normalized = Car.normalizePlate(licensePlate)
        if (normalized.length < 4) {
            return null
        }
        const licenseStart = normalized.substring(0, 3)
        const q = query(collectionGroup(this.firestore, Collections.Cars), orderBy("plateNum"), startAt(licenseStart), endAt(licenseStart + "~"))
        const snapshot = await getDocs(q)
        if (snapshot.empty) {
            return null
        }

        const licenseEnd = normalized.substring(normalized.length - 4)
        let car: Car | null = null
        let userId: string | null = null
        snapshot.forEach(doc => {
            const tmpCar = Car.fromData(doc)
            if (Car.normalizePlate(tmpCar?.plateNum ?? "").endsWith(licenseEnd)) {
                car = tmpCar
                userId = doc.ref.parent!.parent!.id
            }
        })

        if (car) {
            const user = await this.fetchUserData(userId!)
            return [user!, car!]
        } else {
            return null
        }
    }

    generateUserId = () => {
        return doc(collection(this.firestore, Collections.Users)).id
    }

    // *** Admin ***
    fetchAdminPrivileges = async (
        userId: string,
        user: User | undefined = undefined
    ): Promise<{ locationsIds: string[]; companies: string[]; fleetIds: string[] }> => {
        const ref = doc(this.firestore, Collections.Admins, userId)
        const querySnapshot = await getDoc(ref)
        if (!querySnapshot.exists()) {
            return { locationsIds: [], companies: [], fleetIds: [] }
        }
        const data = querySnapshot.data()
        if (user) {
            user.adminLocations = data?.locations ?? []
            user.adminCompanies = data?.companies ?? []
            user.adminFleets = data?.fleets ?? []
        }
        return { locationsIds: data?.locations ?? [], companies: data?.companies ?? [], fleetIds: data?.fleets ?? [] }
    }

    setAdminLocations = (user: User, companiesIds: string[], locationsIds: string[], fleetIds: string[]) => {
        const ref = doc(this.firestore, Collections.Admins, user.uid)
        if (locationsIds.length === 0) {
            companiesIds = []
        } else if (companiesIds.length === 0) {
            companiesIds = [user.company]
        }
        return setDoc(
            ref,
            {
                companies: companiesIds,
                locations: locationsIds,
                fleets: fleetIds,
                email: user.email
            },
            { merge: true }
        )
    }

    // *** Locations ***
    fetchLocation = async (locationCode: string) => {
        if (locationCode.substring(0, 7) === "visitor") {
            return null
        }
        const ref = doc(this.firestore, Collections.Parking, locationCode)
        const documentSnapshot = await getDoc(ref)
        return Location.fromData(documentSnapshot)
    }

    // *** Companies ***
    fetchCompany = async (companyCode: string) => {
        const ref = doc(this.firestore, Collections.Companies, companyCode)
        const documentSnapshot = await getDoc(ref)
        return Company.fromData(documentSnapshot)
    }

    // *** Reservations API ***
    setReservation = (location: Location, reservation: Reservation, sendEmail?: boolean) => {
        const col = collection(this.firestore, Collections.Parking, location.id, Collections.Reservations)
        const reservationId = reservation.id ?? doc(col).id
        const ref = doc(col, reservationId)
        let data = reservation.asData() as Record<string, any>
        data.sendEmail = sendEmail ?? false
        return setDoc(ref, data, { merge: true })
    }

    /**
     * Fetches all reservations for a given location and date range, including both day and permanent reservations.
     * Permanent reservations are expanded to cover each day in the range.
     * Permanent reservations and it's day reservation instance have to have the same start time (granularity to minute).
     * The end time can differ in case a user ends the reservation early.
     * @param location The location of the reservations.
     * @param startDate The start date of the range.
     * @param endDate The end date of the range.
     * @param userId Optional user ID to filter reservations by user.
     * @param transaction Optional Firestore transaction to use for the query.
     * @returns An array of reservations for the specified location and date range.
     */
    fetchAllReservations = async (location: Location, startDate: Date, endDate: Date, userId?: string) => {
        const startOfStartDay = moment.tz(startDate, location.timezone).startOf("d").toDate()
        const endOfEndDay = moment.tz(endDate, location.timezone).endOf("d").toDate()

        const [dayReservations, permanentReservations] = await Promise.all([
            this.fetchDayReservations(location, startOfStartDay, endOfEndDay, userId),
            this.fetchPermanentReservations(location, userId)
        ])

        const expandedPermanentReservations = permanentReservations
            .map(it => this.expandPermanentReservation(location, it, startOfStartDay, endOfEndDay))
            .flat()

        const filteredPermanent = expandedPermanentReservations.filter(
            permanentRes =>
                !dayReservations.some(
                    it =>
                        it.userId === permanentRes.userId &&
                        it.parkingSpotId === permanentRes.parkingSpotId &&
                        moment(it.startTime).isSame(moment(permanentRes.startTime), "minute")
                )
        )

        const allReservations = [dayReservations, filteredPermanent].flat()

        return allReservations.filter(it => it.endTime > startDate && it.startTime < endDate)
    }

    fetchAllReservationsWithUsers = async (location: Location, startDate: Date, endDate: Date, includePrivate: boolean = false) => {
        const reservations = await this.fetchAllReservations(location, startDate, endDate)
        return await this.fetchUsersForReservations(reservations, includePrivate)
    }

    fetchVisitorDayReservations = async (location: Location, startDate: Date, endDate: Date): Promise<VisitorReservation[]> => {
        const q = query(
            collection(this.firestore, Collections.Parking, location.id, Collections.Reservations),
            where("endTime", ">", startDate),
            where("startTime", "<", endDate)
        )

        const querySnapshot = await getDocs(q)
        const visitorDocs = querySnapshot.docs.filter(d => d.data().priority === UserPriorityLevel.Visitors)
        return visitorDocs.map(VisitorReservation.fromData).filterUndef()
    }

    fetchVisitorsReservations = async (
        location: Location,
        startDate: Date,
        endDate: Date,
        includePrivate: boolean = false,
        fillInvitedBy: boolean = true
    ) => {
        const startOfStartDay = moment.tz(startDate, location.timezone).startOf("d").toDate()
        const endOfEndDay = moment.tz(endDate, location.timezone).endOf("d").toDate()
        const visitorReservations = await this.fetchVisitorDayReservations(location, startOfStartDay, endOfEndDay)
        const validVisitorsReservations = (await this.fetchUsersForReservations(visitorReservations, includePrivate)).filter(it => !it.canceled)

        if (fillInvitedBy) {
            const uniqueUserIds = Array.from(
                new Set(validVisitorsReservations.map(it => it.invitedBy).filter((id): id is string => typeof id === "string"))
            )

            const invitedByUsers = await this.fetchUsersWithPrivateData(uniqueUserIds)

            return { visitorsReservations: validVisitorsReservations, invitedByUsers }
        } else {
            return { visitorsReservations: validVisitorsReservations }
        }
    }

    fetchUsersForReservations = <R extends Reservation | FleetReservation>(reservations: R[], includePrivate: boolean = false) => {
        const userIds = Array.from(new Set(reservations.map(reservation => reservation.userId)))
        const promises = userIds.map(id => this.fetchUserDataWithCars(id, includePrivate))
        return Promise.all(promises).then(users => {
            for (const reservation of reservations) {
                reservation.user = users.find(user => user?.uid === reservation.userId) ?? undefined
            }
            return reservations
        })
    }

    fetchPermanentReservations = async (location: Location, userId?: string) => {
        let q = query(collection(this.firestore, Collections.Parking, location.id, Collections.PermanentReservations))
        if (userId) {
            q = query(q, where("userId", "==", userId))
        }

        const querySnapshot = await getDocs(q)
        const reservations = querySnapshot.docs
            .map(PermanentReservation.fromData)
            .filter(r => r)
            .map(r => r!)
        return reservations.filter(r => !r.underlyingReservation.canceled)
    }

    fetchDayReservations = async (location: Location, startDate: Date, endDate: Date, userId?: string): Promise<Reservation[]> => {
        let q = query(collection(this.firestore, Collections.Parking, location.id, Collections.Reservations))
        if (userId) {
            q = query(q, where("userId", "==", userId))
        }
        q = query(q, where("endTime", ">", startDate), where("startTime", "<", endDate))

        const querySnapshot = await getDocs(q)
        return querySnapshot.docs.map(Reservation.fromData).filterUndef()
    }

    fetchReservationId = (locationCode: string, reservationId: string) => {
        const ref = doc(this.firestore, Collections.Parking, locationCode, Collections.Reservations, reservationId)
        return getDoc(ref).then(documentSnapshot => {
            return new Promise<Reservation | null>(resolve => resolve(Reservation.fromData(documentSnapshot)))
        })
    }

    expandPermanentReservation = (location: Location, permanentReservation: PermanentReservation, startDate: Date, endDate: Date): Reservation[] => {
        const firstDay = [startDate, permanentReservation.validFrom].filterUndef().sort((a, b) => b.getTime() - a.getTime())[0]
        const lastDay = [endDate, permanentReservation.validTo].filterUndef().sort((a, b) => a.getTime() - b.getTime())[0]

        const startTimeOriginal = moment.tz(permanentReservation.underlyingReservation.startTime, location.timezone)
        const endTimeOriginal = moment.tz(permanentReservation.underlyingReservation.endTime, location.timezone)

        const result: Reservation[] = []
        const daysCount = moment(lastDay).diff(moment(firstDay), "days")

        for (let i = 0; i <= daysCount; i++) {
            const baseDate = moment.tz(firstDay, location.timezone).add(i, "days")
            baseDate.set({ hour: 10, minute: 0, second: 0, millisecond: 0 })

            const startTime = baseDate.clone().set({
                hour: startTimeOriginal.hour(),
                minute: startTimeOriginal.minute(),
                second: startTimeOriginal.second(),
                millisecond: 0
            })

            const endTime = baseDate.clone().set({
                hour: endTimeOriginal.hour(),
                minute: endTimeOriginal.minute(),
                second: endTimeOriginal.second(),
                millisecond: 0
            })

            const reservation = new Reservation({ ...permanentReservation.underlyingReservation })
            reservation.id = doc(collection(this.firestore, Collections.Parking, location.id, Collections.Reservations)).id
            reservation.startTime = startTime.toDate()
            reservation.endTime = endTime.toDate()
            reservation.confirmed = true
            reservation.priority = 9
            reservation.timestampCreated = moment().toDate()
            result.push(reservation)
        }
        return result
    }

    cancelReservationId = (locationCode: string, reservationId: string) => {
        const ref = doc(this.firestore, Collections.Parking, locationCode, Collections.Reservations, reservationId)
        return updateDoc(ref, {
            canceled: true,
            status: ReservationStatus.Canceled,
            dateCanceled: new Date()
        }).catch(error => {
            console.warn("Error updating reservation: ", error)
        })
    }

    // Goes through the admin API rather than writing to Firestore directly: canceling a permanent reservation also
    // has to cancel the route generated with it, otherwise the car stays undeletable (CarInUse) forever.
    cancelPermanentReservation = async (locationId: string, reservation: Reservation) => {
        const requestBody = {
            targetLocationId: locationId,
            permanentReservationId: reservation.id
        }

        const { data } = await this.callAdminApi("cancelPermanentReservation", requestBody)

        if (data.status !== "OK") {
            if (data.errorCode !== undefined) {
                throw new ApiError(data.description ?? "Cancel permanent reservation error " + ApiErrorCode[data.errorCode], data.errorCode)
            }
            throw new Error(`Unknown cancel permanent reservation error: ${data}`)
        }
    }

    cancelOrEndReservation = (location: Location, reservation: Reservation) => {
        const now = new Date()
        if (reservation.startTime > now) {
            reservation.canceled = true
            reservation.dateCanceled = now
        } else {
            reservation.endTime = now
            reservation.dateCanceled = now
        }

        const ref = doc(this.firestore, Collections.Parking, location.id, Collections.Reservations, reservation.id!)
        return setDoc(ref, reservation.asData(), { merge: true }).catch(error => {
            console.warn("Error updating reservation: ", error)
        })
    }

    fetchAvailableSpots = (location: Location, startDate: Date, endDate: Date, includeDisabled: boolean = false): Promise<ParkingSpot[]> => {
        return Promise.all([this.fetchParkingSpots(location.id, includeDisabled), this.fetchAllReservations(location, startDate, endDate)]).then(
            ([spots, reservations]) => {
                const bookedSpotIds: string[] = reservations.filter(r => r.parkingSpotId && !r.canceled).map(r => r.parkingSpotId!)
                const availableSpots = spots
                    .filter(spot => bookedSpotIds.find(bookedSpotId => spot.id === bookedSpotId) === undefined)
                    .filter(it => it.isEnabled)
                availableSpots.sort(ParkingSpot.compare)
                return Promise.resolve(availableSpots)
            }
        )
    }

    reserveSpot = async (user: User, startDate: Date, endDate: Date, car: Car, preventDuplicite: boolean): Promise<ReservationResult> => {
        if (user.activeLocation === undefined) {
            throw new Error("User has no activeLocation!")
        }
        const requestBody = {
            userId: user.uid,
            locationId: user.activeLocation.id,
            startDate: startDate.getTime() / 1000,
            endDate: endDate.getTime() / 1000,
            priority: user.priority,
            carId: car.id,
            engineType: car.engineType,
            preventDuplicite: preventDuplicite
        }

        const { data } = await this.callClientApi("reserveSpot", requestBody)

        if (data.status !== "OK") {
            if (data.errorCode !== undefined) {
                throw new ApiError("Reserve spot error " + ApiErrorCode[data.errorCode], data.errorCode)
            }
            throw new Error(`Unknown reserve spot error: ${data}`)
        }

        if (data.confirmed === true && data.parkingSpot && data.parkingSpotId && data.reservationId) {
            return new ReservationResult(ReservationResultState.Confirmed, data.parkingSpot, data.parkingSpotId, data.reservationId)
        } else if (data.confirmed === false) {
            return new ReservationResult(ReservationResultState.Declined)
        } else {
            return new ReservationResult(ReservationResultState.Pending, undefined, undefined, data.reservationId)
        }
    }

    createReservation = async (
        userId: string,
        locationId: string,
        startDate: Date,
        endDate: Date,
        carId: string,
        priority: UserPriorityLevel,
        spot: ParkingSpot
    ) => {
        const requestBody = {
            targetUserId: userId,
            targetLocationId: locationId,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            priority,
            carId: carId,
            parkingSpot: spot.name,
            parkingSpotId: spot.id
        }

        const { data } = await this.callAdminApi("createReservation", requestBody)

        if (data.status !== "OK") {
            if (data.errorCode !== undefined) {
                throw new ApiError("Reserve spot error " + ApiErrorCode[data.errorCode], data.errorCode)
            }
            throw new Error(`Unknown reserve spot error: ${data}`)
        }

        return
    }

    createPermanentReservation = async (
        userId: string,
        locationId: string,
        startDate: Date,
        endDate: Date,
        parkingSpotId: string,
        carId: string,
        validFrom?: Date,
        validTo?: Date
    ) => {
        const requestBody = {
            targetUserId: userId,
            targetLocationId: locationId,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            parkingSpotId,
            carId,
            validFrom: validFrom?.toISOString() ?? null,
            validTo: validTo?.toISOString() ?? null
        }

        const { data } = await this.callAdminApi("createPermanentReservation", requestBody)

        if (data.status !== "OK") {
            if (data.errorCode !== undefined) {
                throw new ApiError(data.description ?? "Create permanent reservation error " + ApiErrorCode[data.errorCode], data.errorCode)
            }
            throw new Error(`Unknown create permanent reservation error: ${data}`)
        }

        return data.permanentReservationId as string
    }

    reserveFleetCar = async (
        userId: string,
        fleetId: string,
        startDate: Date,
        endDate: Date,
        fleetCarId: string,
        note?: string
    ): Promise<FleetReservationResult> => {
        const requestBody = {
            userId: userId,
            fleetId: fleetId,
            startDate: startDate.getTime() / 1000,
            endDate: endDate.getTime() / 1000,
            fleetCarId,
            note
        }

        const { data } = await this.callClientApi("reserveFleetCar", requestBody)

        if (data.status !== "OK") {
            if (data.errorCode !== undefined) {
                throw new ApiError("Reserve fleet car error " + ApiErrorCode[data.errorCode], data.errorCode)
            }
            throw new Error(`Unknown reserve fleet car error: ${data}`)
        }

        if (data.confirmed === true && data.fleetReservationId) {
            return new FleetReservationResult(ReservationResultState.Confirmed, data.fleetReservationId)
        } else if (data.confirmed === false) {
            return new FleetReservationResult(ReservationResultState.Declined)
        }
        throw new Error(`Unrecognized reserve fleet car response: ${JSON.stringify(data)}`)
    }

    cancelFleetReservation = async (fleetId: string, fleetReservationId: string): Promise<void> => {
        const requestBody = {
            fleetId: fleetId,
            fleetReservationId: fleetReservationId
        }

        const { data } = await this.callClientApi("cancelFleetReservation", requestBody)

        if (data.status !== "OK") {
            if (data.errorCode !== undefined) {
                throw new ApiError("Cancel fleet reservation error " + ApiErrorCode[data.errorCode], data.errorCode)
            }
            throw new Error(`Unknown cancel fleet reservation error: ${data}`)
        }
    }

    reportAbuse = async (
        locationId: string,
        reservationId: string,
        plateNum: string,
        pictureFilename: string = ""
    ): Promise<{ abuseId: string; confirmed: boolean; reservationId: string }> => {
        const requestBody = {
            // Hand-typed by whoever reports the incident, so normalise before the backend matches it
            // against stored plates - this is the lookup that names the offending driver.
            plateNum: Car.normalizePlate(plateNum),
            locationId: locationId,
            reservationId: reservationId,
            pictureFilename: pictureFilename
        }

        const { data } = await this.callClientApi("reportAbuse", requestBody)

        if (data.status !== "OK") {
            if (data.errorCode !== undefined) {
                throw new ApiError("Report abuse error " + ApiErrorCode[data.errorCode], data.errorCode)
            }
            throw new Error(`Unknown report abuse error: ${data}`)
        }
        return {
            abuseId: data.abuseId,
            confirmed: data.confirmed,
            reservationId: data.reservationId
        }
    }

    cancelOrEndReservationAPI = async (locationId: string, reservationId: string): Promise<void> => {
        const requestBody = {
            reservationId: reservationId,
            locationId: locationId
        }

        const { data } = await this.callClientApi("cancelOrEndReservation", requestBody)

        if (data.status !== "OK") {
            if (data.errorCode !== undefined) {
                throw new ApiError("Cancel or end reservation error " + ApiErrorCode[data.errorCode], data.errorCode)
            }
            throw new Error(`Unknown cancel or end reservation error: ${data}`)
        }
    }

    // *** ParkingSpots API ***
    fetchParkingSpots = (locationCode: string, includeDisabled: boolean = false) => {
        let q = query(collection(this.firestore, Collections.Parking, locationCode, Collections.Spots))
        return getDocs(q).then(querySnapshot => {
            const spots: ParkingSpot[] = []
            querySnapshot.forEach(doc => {
                const spot = ParkingSpot.fromData(doc)
                if (spot && (includeDisabled || spot.isEnabled)) {
                    spots.push(spot)
                }
            })
            return Promise.resolve(spots)
        })
    }

    /**
     * Creates parking spots through the `adminApi` createParkingSpots callable, which validates the
     * payload (the size against the ParkingSize enum) and authorizes on targetLocationId.
     *
     * This replaced a direct Firestore write that could only ever set a name and the electric flag, so
     * every other field fell back to whatever the reader defaulted to. Do not reintroduce one.
     */
    createParkingSpotsAPI = async (locationId: string, spots: { name: string; electric: boolean; size: ParkingSize }[]): Promise<void> => {
        const requestBody = {
            targetLocationId: locationId,
            spots: spots
        }

        const { data } = await this.callAdminApi("createParkingSpots", requestBody)

        if (data.status !== "OK") {
            if (data.errorCode !== undefined) {
                throw new ApiError("Create parking spots error " + ApiErrorCode[data.errorCode], data.errorCode)
            }
            throw new Error(`Unknown create parking spots error: ${data}`)
        }
    }

    // Editing an existing spot stays a direct write: the backend exposes createParkingSpots and
    // deleteParkingSpots on adminApi, but no update counterpart.
    setParkingSpot = (locationCode: string, spot: ParkingSpot) => {
        const ref = doc(this.firestore, Collections.Parking, locationCode, Collections.Spots, spot.id)
        return setDoc(
            ref,
            {
                timestampUpdated: serverTimestamp(),
                name: spot.name,
                electric: spot.electric,
                attractivity: spot.attractivity,
                naturalGasAllowed: spot.naturalGasAllowed,
                isEnabled: spot.isEnabled,
                inReserve: spot.inReserve,
                size: spot.size ?? ParkingSize.Regular
            },
            { merge: true }
        )
    }

    removeParkingSpot = (locationCode: string, spotId: string) => {
        const ref = doc(this.firestore, Collections.Parking, locationCode, Collections.Spots, spotId)
        return deleteDoc(ref)
    }

    // *** Abuse API ***
    uploadAbuseImage = (locationCode: string, imageData: string, abuseId: string) => {
        const storageRef = ref(this.storage, "parkingAbuses/" + locationCode + "/" + abuseId)
        return uploadString(storageRef, imageData, "data_url")
    }

    getAbuseImageLink = (locationCode: string, fileName: string) => {
        const storageRef = ref(this.storage, "parkingAbuses/" + locationCode + "/" + fileName)
        return getDownloadURL(storageRef).catch(e => undefined)
    }

    getSpotImageLink = (locationCode: string, imageFilename: string) => {
        const storageRef = ref(this.storage, "parking/" + locationCode + "/" + imageFilename + ".svg")
        return getDownloadURL(storageRef).catch(e => undefined)
    }

    fetchAllAbuses = async (locationCode: string) => {
        let q = query(collection(this.firestore, Collections.Parking, locationCode, Collections.Abuses))
        const snapshot = await getDocs(q)
        const abuses = snapshot.docs.map(doc => AbuseInfo.fromData(doc))
        return abuses.filter(a => a).map(a => a!)
    }

    // *** Slack ***
    updateSlackData = async (userId: string, accessToken: string, slackUserId: string) => {
        const ref = doc(this.firestore, Collections.Users, userId)
        return updateDoc(ref, {
            slackData: {
                accessToken,
                userId: slackUserId
            }
        })
    }

    // *** Fleet API ***
    fetchFleet = async (fleetId: string) => {
        const ref = doc(this.firestore, Collections.Fleet, fleetId)
        const documentSnapshot = await getDoc(ref)
        return Fleet.fromData(documentSnapshot)
    }

    setUserActiveFleet = (user: User, fleetId: string, providedFleets: Fleet[] | undefined = undefined) => {
        const oldFleets = user.fleetIds ?? []
        let fleets = [fleetId]
        fleets = fleets.concat(oldFleets.filter(l => l !== fleetId))
        const fleetsData = [...(user.fleets ?? []), ...(providedFleets ?? [])]
        user.fleets = fleets.map(fleetId => fleetsData.find(f => f.id === fleetId)).filterUndef()
        user.fleetIds = user.fleets.map(f => f.id)
        const ref = doc(this.firestore, Collections.Users, user.uid)
        const data = { fleets: fleets }
        return setDoc(ref, data, { merge: true })
    }

    fetchAllFleetCars = async (fleetId: string) => {
        let q = query(collection(this.firestore, Collections.Fleet, fleetId, Collections.FleetCars))
        const snapshot = await getDocs(q)
        const cars = snapshot.docs.map(doc => FleetCar.fromData(doc))
        return cars.filterUndef()
    }

    fetchAllFleetReservations = async (fleetId: string, startDate: Date, endDate: Date, userId?: string) => {
        let q = query(collection(this.firestore, Collections.Fleet, fleetId, Collections.FleetReservations))
        if (userId) {
            q = query(q, where("userId", "==", userId))
        }
        q = query(q, where("endTime", ">", startDate), where("startTime", "<", endDate))

        const querySnapshot = await getDocs(q)
        return querySnapshot.docs.map(FleetReservation.fromData).filterUndef()
    }

    fetchAllFleetReservationsWithUsers = async (fleetId: string, startDate: Date, endDate: Date, includePrivate: boolean = false) => {
        const reservations = await this.fetchAllFleetReservations(fleetId, startDate, endDate)
        return await this.fetchUsersForReservations(reservations, includePrivate)
    }

    updateFleetReservation = async (fleetId: string, fleetReservationId: string, data: { [key: string]: any }) => {
        const ref = doc(this.firestore, Collections.Fleet, fleetId, Collections.FleetReservations, fleetReservationId)
        return updateDoc(ref, data)
    }

    fetchAvailableCars = async (fleetId: string, startDate: Date, endDate: Date): Promise<FleetCar[]> => {
        const [fleetCars, fleetReservations] = await Promise.all([
            this.fetchAllFleetCars(fleetId),
            this.fetchAllFleetReservations(fleetId, startDate, endDate)
        ])
        const bookedCarIds = fleetReservations.filter(r => r.fleetCarId && !r.canceled).map(r => r.fleetCarId!)
        const availableCars = fleetCars.filter(car => bookedCarIds.find(bookedCarId => car.id === bookedCarId) === undefined)
        availableCars.sort((a, b) => a.title.localeCompare(b.title))

        return availableCars
    }

    addFleetCar = (fleetId: string, carTitle: string, plateNum: string, engineType: EngineType, note: string, odometer: number) => {
        let ref = doc(collection(this.firestore, Collections.Fleet, fleetId, Collections.FleetCars))
        // Normalised here rather than relying on FleetCar.asData: this app pins its own shared-models
        // checkout, so the model-level normalisation only reaches it once the submodule pointer is bumped.
        const fleetCar = new FleetCar({
            id: ref.id,
            title: carTitle,
            plateNum: Car.normalizePlate(plateNum),
            engineType,
            note,
            timestampCreated: new Date(),
            odometer: odometer
        })
        return setDoc(ref, fleetCar.asData())
    }

    updateFleetCar = async (fleetId: string, fleetCarId: string, data: { [key: string]: any }) => {
        const ref = doc(this.firestore, Collections.Fleet, fleetId, Collections.FleetCars, fleetCarId)
        return updateDoc(ref, data)
    }

    removeFleetCar = (fleetId: string, fleetCarId: string) => {
        const ref = doc(this.firestore, Collections.Fleet, fleetId, Collections.FleetCars, fleetCarId)
        return deleteDoc(ref)
    }

    // *** Callable ***
    callClientApi = async (method: string, body: any) => {
        const callable = httpsCallable<any, any>(this.functions, "clientApi")
        return await callable({
            method,
            apiVersion: 1,
            body,
            header: {
                platform: "web",
                product: "garage-web-app",
                version: packageJson.version
            }
        })
    }

    // Admin-only operations (e.g. createReservation, enableDisableUser) are dispatched through the
    // dedicated `adminApi` callable. Authorization is by a target* field in the body (e.g. targetUserId).
    callAdminApi = async (method: string, body: any) => {
        const callable = httpsCallable<any, any>(this.functions, "adminApi")
        return await callable({
            method,
            apiVersion: 1,
            body,
            header: {
                platform: "web",
                product: "garage-web-app",
                version: packageJson.version
            }
        })
    }
}

export default Firebase
export { Timestamp }

export const areSameDay = (first: Date, second: Date = new Date()) =>
    first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate()
